import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUnreadNotificationCount } from '@features/notifications/services/notificationsApi';
import useToken from '../hooks/useToken';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token } = useToken();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await getUnreadNotificationCount(token);
      setUnreadCount(data.count);
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateUnreadCount = (newCount) => {
    setUnreadCount(newCount);
  };

  const incrementUnreadCount = () => {
    setUnreadCount(prev => prev + 1);
  };

  const decrementUnreadCount = () => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const resetUnreadCount = () => {
    setUnreadCount(0);
  };

  useEffect(() => {
    fetchUnreadCount();
    
    // Set up polling for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [token, fetchUnreadCount]);

  const value = {
    unreadCount,
    loading,
    fetchUnreadCount,
    updateUnreadCount,
    incrementUnreadCount,
    decrementUnreadCount,
    resetUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
